import { useEffect, useState } from "react"
import { Phone, MessageCircle, Heart, Users, Shield, BookOpen, ChevronRight } from "lucide-react"

const CRISIS_RESOURCES = [
  {
    label: 'Emergency Services',
    number: '911',
    description: 'Immediate danger or life-threatening situation',
    icon: Phone,
    href: 'tel:911',
  },
  {
    label: 'Crisis Text Line',
    number: 'Text HOME to 741741',
    description: 'Free, 24/7 mental health support via text',
    icon: MessageCircle,
    href: 'sms:741741?body=HOME',
  },
]

const MENTAL_HEALTH = [
  {
    title: 'School Counselor',
    description: 'Available Mon–Fri, 8:00 AM – 4:00 PM',
    action: 'Schedule Appointment',
    icon: Heart,
    iconColor: 'text-pink-500',
    dmIconBg: 'bg-pink-900/40',
    lmIconBg: 'bg-pink-100',
  },
  {
    title: 'Peer Support Group',
    description: 'Connect with fellow students in a safe space',
    action: 'Join Group',
    icon: Users,
    iconColor: 'text-purple-500',
    dmIconBg: 'bg-purple-900/40',
    lmIconBg: 'bg-purple-100',
  },
]

const SAFETY_TIPS = [
  {
    title: 'Password Security',
    body: 'Use unique, strong passwords. Never share them with friends.',
    icon: Shield,
    iconColor: 'text-blue-500',
    dmIconBg: 'bg-blue-900/40',
    lmIconBg: 'bg-blue-100',
  },
  {
    title: 'Privacy Settings',
    body: 'Check social media privacy settings and keep profiles private.',
    icon: BookOpen,
    iconColor: 'text-indigo-500',
    dmIconBg: 'bg-indigo-900/40',
    lmIconBg: 'bg-indigo-100',
  },
  {
    title: 'Stranger Safety',
    body: 'Never share personal info (address, phone, school) with strangers online.',
    icon: Users,
    iconColor: 'text-blue-500',
    dmIconBg: 'bg-blue-900/40',
    lmIconBg: 'bg-blue-100',
  },
]

export function StudentSupport() {
  const [dm, setDm] = useState(() => localStorage.getItem('studentDarkMode') === 'true')

  useEffect(() => {
    const handler = (e: Event) => setDm((e as CustomEvent<{ darkMode: boolean }>).detail.darkMode)
    window.addEventListener('studentDarkModeChanged', handler)
    return () => window.removeEventListener('studentDarkModeChanged', handler)
  }, [])

  const card = dm
    ? "bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 shadow-sm"
    : "bg-white rounded-2xl border border-gray-100 shadow-sm"

  return (
    <div className="px-4 py-5 space-y-6">

      {/* ── Emergency CTA card ── */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-rose-500 to-red-600 p-5 text-white shadow-lg shadow-rose-200">
        <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full pointer-events-none" />
        <div className="absolute -bottom-6 -left-4 w-24 h-24 bg-white/10 rounded-full pointer-events-none" />
        <div className="relative">
          <p className="text-rose-100 text-xs font-medium uppercase tracking-wide mb-1">
            Need help right now?
          </p>
          <h2 className="text-xl font-bold mb-1">Crisis Support</h2>
          <p className="text-rose-100 text-sm mb-5 leading-relaxed">
            You are never alone. Help is available 24/7 — reach out at any time.
          </p>
          <div className="space-y-2.5">
            {CRISIS_RESOURCES.map(({ label, number, description, icon: Icon, href }) => (
              <a
                key={label}
                href={href}
                className="flex items-center gap-3 bg-white/15 hover:bg-white/25 rounded-2xl px-4 py-3 transition-colors no-underline"
              >
                <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm">{label}</p>
                  <p className="text-rose-100 text-xs truncate">{number}</p>
                  <p className="text-rose-200 text-[10px] leading-tight">{description}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-rose-200 flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* ── Mental health ── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${dm ? 'bg-pink-900/40' : 'bg-pink-100'}`}>
            <Heart className="w-4 h-4 text-pink-500" />
          </div>
          <h2 className={`font-semibold ${dm ? 'text-white' : 'text-gray-900'}`}>Mental Health Support</h2>
        </div>
        <div className="space-y-3">
          {MENTAL_HEALTH.map(({ title, description, action, icon: Icon, iconColor, dmIconBg, lmIconBg }) => (
            <div key={title} className={`${card} p-4 flex items-center gap-4`}>
              <div className={`w-10 h-10 ${dm ? dmIconBg : lmIconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-5 h-5 ${iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-semibold text-sm ${dm ? 'text-white' : 'text-gray-900'}`}>{title}</p>
                <p className={`text-xs mt-0.5 ${dm ? 'text-white/50' : 'text-gray-500'}`}>{description}</p>
              </div>
              <button className="btn-glass btn-glass-pill btn-glass-blue flex-shrink-0 text-xs" style={{ border: 'none' }}>
                {action}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ── Quick contacts ── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${dm ? 'bg-purple-900/40' : 'bg-purple-100'}`}>
            <Users className="w-4 h-4 text-purple-500" />
          </div>
          <h2 className={`font-semibold ${dm ? 'text-white' : 'text-gray-900'}`}>Quick Contacts</h2>
        </div>
        <div className={`${card} overflow-hidden`}>
          {[
            { label: 'School Safety Officer', action: 'Contact' },
            { label: 'IT Support',            action: 'Contact' },
            { label: 'Parent / Guardian',     action: 'Call'    },
          ].map(({ label, action }, idx, arr) => (
            <div
              key={label}
              className={`flex items-center justify-between px-4 py-3.5 ${
                idx < arr.length - 1 ? dm ? 'border-b border-white/10' : 'border-b border-gray-50' : ''
              }`}
            >
              <p className={`text-sm font-medium ${dm ? 'text-white' : 'text-gray-900'}`}>{label}</p>
              <button className="btn-glass btn-glass-pill text-xs" style={{ border: 'none' }}>
                {action}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ── Safety tips ── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${dm ? 'bg-emerald-900/40' : 'bg-emerald-100'}`}>
            <Shield className="w-4 h-4 text-emerald-500" />
          </div>
          <h2 className={`font-semibold ${dm ? 'text-white' : 'text-gray-900'}`}>Online Safety Tips</h2>
        </div>
        <div className="space-y-2.5">
          {SAFETY_TIPS.map(({ title, body, icon: Icon, iconColor, dmIconBg, lmIconBg }) => (
            <div
              key={title}
              className={`rounded-2xl border p-4 flex items-start gap-3 ${
                dm ? 'bg-white/10 border-white/10' : 'bg-blue-50 border-blue-100'
              }`}
            >
              <div className={`${dm ? dmIconBg : lmIconBg} w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5`}>
                <Icon className={`w-4 h-4 ${iconColor}`} />
              </div>
              <div>
                <p className={`font-semibold text-sm ${dm ? 'text-white' : 'text-gray-900'}`}>{title}</p>
                <p className={`text-xs mt-0.5 leading-relaxed ${dm ? 'text-white/60' : 'text-gray-600'}`}>{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="h-2" />
    </div>
  )
}
